import { C as Cart } from './cart-BA5kDYZV.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';

/**
 * ProductGridSwitcher component that allows switching between different product grid layouts
 * and persists the selected layout in the cart attributes.
 */
class ProductGridSwitcher extends HTMLElement {
  /** @type {string} */
  layout;
  /** @type {NodeListOf<HTMLButtonElement>} */
  switchButtons;
  /** @type {HTMLElement} */
  collectionGrid;
  /** @type {NodeListOf<HTMLImageElement>} */
  productImages;
  /** @type {NodeListOf<HTMLElement>} The promo blocks */
  promoBlocks;

  connectedCallback() {
    this.switchButtons = this.querySelectorAll('button');
    this.collectionGrid = document.querySelector('product-grid');
    this.productImages = this.collectionGrid.querySelectorAll('.product-card__image');
    this.promoBlocks = this.collectionGrid.querySelectorAll('.promo-block');

    // Initialize layout from product-grid data-layout attribute
    this.layout = this.collectionGrid.dataset.layout || 'default';
    this.setActiveButton(this.layout);

    this.switchButtons.forEach((button) => {
      button.addEventListener('click', () => this.onClick(button));
    });

    Pubsub.subscribe('collection:loadPreviousProducts', () => this.handleLoadPreviousProducts());
  }

  /**
   * Handle load previous products event by showing promo blocks and updating their layout
   */
  handleLoadPreviousProducts() {
    this.promoBlocks = this.collectionGrid.querySelectorAll('.promo-block');

    this.updatePromoBlocks(this.layout);
  }

  /**
   * Handle switch button click
   * @param {HTMLButtonElement} button - The clicked button
   */
  onClick(button) {
    const { layout, imageSizes } = button.dataset;
    this.layout = layout;

    this.collectionGrid.dataset.layout = layout;
    this.setActiveButton(layout);
    this.updateCart();
    this.updateImageSizes(imageSizes);
    this.updatePromoBlocks(layout);

    // Publish layout change event
    Pubsub.publish('collection:layoutChanged', { layout });
  }

  /**
   * Set active button state
   * @param {string} activeLayout - The active layout
   */
  setActiveButton(activeLayout) {
    this.switchButtons.forEach((button) => {
      const { layout } = button.dataset;
      button.classList.toggle('button--white', layout !== activeLayout);
      button.classList.toggle('button--black', layout === activeLayout);
    });
  }

  /**
   * Update cart attributes with current layout
   */
  async updateCart() {
    try {
      await Cart.update({
        attributes: {
          collection_layout: this.layout,
        },
      });
    } catch (error) {
      console.error("Failed to update cart attributes", error);
      Notyf.error(window.toast_translations.product_grid_switcher.update_cart_error);
    }
  }

  /**
   * Update image sizes
   */
  updateImageSizes(imageSizes) {
    this.productImages.forEach((image) => (image.sizes = imageSizes));
  }

  /**
   * Update promo block positions based on layout
   * @param {string} layout - The active layout
   */
  updatePromoBlocks(layout) {
    //   // Get product count from collection grid
    const productCount = this.collectionGrid.querySelectorAll('.product-card').length;

    // Determine products per row based on layout
    let productsPerRow = 4; // default
    if (layout === 'large') {
      productsPerRow = 2;
    } else if (layout === 'compact') {
      productsPerRow = 6;
    }

    // Calculate total rows in grid
    const modulo = productCount % productsPerRow;
    let rows = Math.floor(productCount / productsPerRow);
    if (modulo > 0 && rows > 0) {
      rows += 1;
    }

    this.promoBlocks.forEach((element) => {
      const gridConfig = JSON.parse(element.dataset.gridConfig || '{}')[layout];
      const image = element.querySelector('img');

      const { id } = element;

      if (!id || !gridConfig) return;

      const { aspectRatio, gridColumn, gridRow, md, imageSizes } = gridConfig;

      // Update data-layout attribute
      element.dataset.layout = layout;

      // Update grid positioning CSS variables
      element.style.setProperty('--aspect-ratio', aspectRatio);
      element.style.setProperty('--grid-column', gridColumn);
      element.style.setProperty('--grid-row', gridRow);
      element.style.setProperty('--grid-column-md', md.gridColumn);
      element.style.setProperty('--grid-row-md', md.gridRow);

      // Update image sizes
      image.sizes = imageSizes;

      // Check if promo block should be shown based on row position
      const rowStart = parseInt(md.gridRow.split('/')[0] || 1);
      const shouldShow = rowStart <= rows;

      // Toggle hidden class
      element.classList.toggle('hidden', !shouldShow);

      // Toggle visibility based on should_show logic
      element.style.display = shouldShow ? '' : 'none';
    });
  }
}

customElements.define('product-grid-switcher', ProductGridSwitcher);
